import { useState } from 'react';
import { clearSession } from '../api/client.js';
import { deleteAccount } from '../api/endpoints.js';
import { clearFlags } from '../flow/machine.js';
import { useAction } from '../api/hooks.js';
import { Button, Failure, Field, Note, Panel, Row, Screen } from '../ui/kit.js';

/**
 * DELETE /v1/me. 204 whether or not the whole protocol completed: from step one
 * the account is irreversibly unusable, so the deletion HAS happened from the
 * caller's point of view. A partial failure changes the operator's summary row,
 * not this answer — and `success: true` never returns with a non-empty error
 * list because the route returns no success body at all.
 *
 * There is no soft-delete window and no confirmation parameter on the endpoint.
 * The typed confirmation below is this client being careful, not the api.
 */
export function AccountScreen() {
  const [confirmation, setConfirmation] = useState('');
  // The deleted account's flow flags must not greet whoever signs in next — the
  // machine's own doc comment claimed deletion cleared them and it never did.
  const remove = useAction(deleteAccount, () => {
    clearFlags();
    clearSession();
  });

  return (
    <Screen
      title="Delete account"
      lede="Irreversible, immediate, and idempotent — deleting an already-deleted account is success, not a 404."
    >
      <Note>
        Deleting confirms the enrolled faces are gone from services BEFORE writing any tombstone
        (P19): the handler calls DeleteFaces, then ListFaces to prove absence, and aborts rather than
        tombstoning if faces remain.
      </Note>

      <Panel title="confirm">
        <p className="muted">
          Type <code>DELETE</code> to enable the button. This gate exists only in this client — the
          endpoint takes no confirmation and no identifier of any kind.
        </p>
        <Row>
          <Field label="confirmation" value={confirmation} onChange={setConfirmation} />
          <Button
            variant="danger"
            onClick={() => remove.run()}
            disabled={remove.pending || confirmation !== 'DELETE'}
          >
            {remove.pending ? 'deleting…' : 'Delete my account'}
          </Button>
        </Row>
        {remove.error !== null && (
          <Failure
            error={remove.error}
            hint={
              remove.error.code === 'DELETION_INCOMPLETE'
                ? 'Part of the protocol did not finish. The account is already unusable and the deletion stays retryable; the per-resource outcomes are in iam.deletion_summaries.'
                : undefined
            }
          />
        )}
      </Panel>
    </Screen>
  );
}
